// src/errors/handlers/axios-error.handler.ts

/**
 * Axios Error Handler
 *
 * WHY: Transform axios errors into typed application errors
 * WHAT: Central handler for all axios/HTTP errors
 * BENEFIT:
 *   - Consistent error types from HTTP requests
 *   - Automatic categorization and severity
 *   - Proper error class instantiation
 *
 * DESIGN DECISIONS:
 * 1. Factory pattern for creating appropriate error type
 * 2. Preserves original error as cause
 * 3. Extracts all available context
 * 4. Handles edge cases (no response, timeout, etc.)
 */

import { AxiosError } from "axios";
import {
  AppError,
  ApiError,
  NetworkError,
  TimeoutError,
  CancellationError,
  AuthenticationError,
  AuthorizationError,
  RateLimitError,
} from "../classes";
import type { ErrorContext } from "../types";
import {
  type ApiErrorResponse,
  isApiErrorResponse,
} from "../types/api-response.types";
import {
  isAxiosError,
  isNetworkError,
  isTimeoutError,
  isCancelledError,
  extractRetryAfter,
} from "../utils/error-parser";
import { errorLogger } from "../utils/error-logger";

/**
 * Options for handling axios errors
 */
export interface AxiosErrorHandlerOptions {
  /** Additional context to include */
  context?: Partial<ErrorContext>;
  /** Log the error (default: true) */
  log?: boolean;
}

/**
 * Axios Error Handler Class
 *
 * Transforms axios errors into appropriate AppError subclasses.
 */
export class AxiosErrorHandler {
  /**
   * Handle any error and return appropriate AppError
   *
   * @param error - Any error (axios or other)
   * @param options - Handler options
   * @returns Appropriate AppError subclass
   */
  static handle(
    error: unknown,
    options: AxiosErrorHandlerOptions = {},
  ): AppError {
    console.log("error in handle", error);
    const { context, log = true } = options;

    // Already an AppError, just add context if provided
    if (error instanceof AppError) {
      if (context) {
        return error.withContext(context);
      }
      return error;
    }

    // Handle axios errors
    if (isAxiosError(error)) {
      const appError = this.handleAxiosError(
        error as AxiosError<ApiErrorResponse>,
        context,
      );

      if (log) {
        errorLogger.logError(appError.serialize());
      }

      return appError;
    }

    // Handle native errors
    if (error instanceof Error) {
      return new AppError(error.message, {
        cause: error,
        context: context ? { ...context, timestamp: new Date() } : undefined,
      });
    }

    // Handle unknown errors
    return new AppError(
      typeof error === "string" ? error : "An unknown error occurred",
      {
        cause: error,
        context: context ? { ...context, timestamp: new Date() } : undefined,
      },
    );
  }

  /**
   * Handle axios-specific errors
   */
  private static handleAxiosError(
    error: AxiosError<ApiErrorResponse>,
    context?: Partial<ErrorContext>,
  ): AppError {
    // Build error context from request
    const errorContext: ErrorContext = {
      ...context,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      requestId: error.config?.headers?.["X-Request-ID"] as string | undefined,
      timestamp: new Date(),
    };

    // Handle cancelled requests
    if (isCancelledError(error)) {
      return new CancellationError(undefined, {
        context: errorContext,
        cause: error,
      });
    }

    // Handle timeout
    if (isTimeoutError(error)) {
      return TimeoutError.fromAxiosTimeout(
        {
          url: error.config?.url,
          timeout: error.config?.timeout,
        },
        error,
      );
    }

    // Handle network errors (no response)
    if (isNetworkError(error)) {
      return NetworkError.fromErrorCode(error.code || "ERR_NETWORK", error);
    }

    // Handle response errors
    if (error.response) {
      return this.handleResponseError(error, errorContext);
    }

    // Fallback for unknown axios errors
    return new NetworkError(error.message, {
      cause: error,
      context: errorContext,
    });
  }

  /**
   * Handle HTTP response errors (4xx, 5xx)
   */
  private static handleResponseError(
    error: AxiosError<ApiErrorResponse>,
    context: ErrorContext,
  ): AppError {
    const status = error.response!.status;
    const data = error.response!.data;

    // Handle specific status codes
    switch (status) {
      case 401:
        return this.handleAuthenticationError(error, data, context);

      case 403:
        return this.handleAuthorizationError(error, data, context);

      case 429:
        return this.handleRateLimitError(error, context);

      default:
        return this.handleGenericApiError(error, data, status, context);
    }
  }

  /**
   * Handle 401 Unauthorized
   */
  private static handleAuthenticationError(
    error: AxiosError<ApiErrorResponse>,
    data: ApiErrorResponse | undefined,
    context: ErrorContext,
  ): AuthenticationError {
    const message = data?.message || "Authentication required";
    const code = data?.code;

    // Check for token expiration
    if (code === "TOKEN_EXPIRED" || code === "ERR_TOKEN_EXPIRED") {
      // return AuthenticationError.tokenExpired(error);
    }

    // Check for invalid token
    if (code === "TOKEN_INVALID" || code === "ERR_TOKEN_INVALID") {
      //return AuthenticationError.tokenInvalid(error);
    }

    // Generic auth error
    return new AuthenticationError(message, {
      context,
      cause: error,
    });
  }

  /**
   * Handle 403 Forbidden
   */
  private static handleAuthorizationError(
    error: AxiosError<ApiErrorResponse>,
    data: ApiErrorResponse | undefined,
    context: ErrorContext,
  ): AuthorizationError {
    const message = data?.message || "Access denied";

    return new AuthorizationError(message, {
      context,
      cause: error,
    });
  }

  /**
   * Handle 429 Rate Limit
   */
  private static handleRateLimitError(
    error: AxiosError<ApiErrorResponse>,
    context: ErrorContext,
  ): RateLimitError {
    const retryAfter = extractRetryAfter(error);

    return new RateLimitError(undefined, {
      retryAfterSeconds: retryAfter,
      context,
      cause: error,
    });
  }

  /**
   * Handle generic API errors
   */
  private static handleGenericApiError(
    error: AxiosError<ApiErrorResponse>,
    data: ApiErrorResponse | undefined,
    status: number,
    context: ErrorContext,
  ): ApiError {
    // Use API response if available
    if (data && isApiErrorResponse(data)) {
      return ApiError.fromResponse(data, error);
    }

    // Create from status code
    return ApiError.fromStatus(status, error, context);
  }
}
